import {
  json,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useFetchers,
  useLoaderData,
  useNavigation,
  useRouteError,
  useMatches,
} from "@remix-run/react";
import type { LinksFunction, LoaderFunctionArgs } from "@remix-run/node";
import * as Sentry from "@sentry/remix";

import "./styles/tailwind.css";
import "./styles/nprogress.css";
import { useEffect } from "react";
import NProgress from 'nprogress';
import { ErrorPage } from '~/components/ErrorPage';
import { CookieBanner } from '~/components/CookieBanner';
import { getOptionalUser } from "~/lib/auth-helpers";
import { getCookieConsent } from "~/lib/.server/cookie";
import { getUserFromMatches } from "~/lib/error-utils";
import { Toaster } from "~/components/ui/toaster";

/**
 * サーバーに送信しないエラーパターン（Remix内部エラーなど）
 * これらのエラーはSentryで補足されるため、/api/errorには送信しない
 */
const IGNORED_CLIENT_ERROR_PATTERNS = [
  /manifest version mismatch/i,
  /Failed to fetch dynamically imported module/i,
  /Loading chunk.*failed/i,
  /Unable to preload CSS/i,
  // Adobe Acrobat等のPDF関連ブラウザ拡張機能が生成する偽陽性エラー
  /Object Not Found Matching Id/i,
  // サーバーサイドレンダリングのタイムアウト/中断エラー（Sentry server-side でキャプチャ済み）
  /The render was aborted by the server/i,
];

/**
 * エラーメッセージがサーバーに送信すべきかどうかを判定
 */
const shouldReportErrorToServer = (message: string): boolean => {
  return !IGNORED_CLIENT_ERROR_PATTERNS.some(pattern => pattern.test(message));
};

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
  { rel: "icon", type: "image/x-icon", href: "/favicon/favicon.ico" },
  { rel: "icon", type: "image/svg+xml", href: "/favicon/favicon.svg" },
  { rel: "apple-touch-icon", href: "/favicon/apple-touch-icon.png" },
  { rel: "manifest", href: "/favicon/site.webmanifest" },
];

export function Layout({ children }: { children: React.ReactNode }) {
  // useMatches()を使ってroot loaderのデータを安全に取得
  const matches = useMatches();
  const rootMatch = matches.find((match) => match.id === 'root');
  const data = rootMatch?.data as { ga4MeasurementId?: string; sentryDsn?: string; hasConsented?: boolean } | undefined;

  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {data?.ga4MeasurementId && data?.hasConsented && (
          <>
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${data.ga4MeasurementId}`}
            />
            <script
              dangerouslySetInnerHTML={{
                __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', '${data.ga4MeasurementId}');
                `,
              }}
            />
          </>
        )}
        {data?.sentryDsn && (
          <script
            dangerouslySetInnerHTML={{
              __html: `window.__sentryDsn = ${JSON.stringify(data.sentryDsn)};`,
            }}
          />
        )}
      </head>
      <body suppressHydrationWarning>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const [user, hasConsented] = await Promise.all([
    getOptionalUser(request),
    getCookieConsent(request),
  ]);
  const currentYear = new Date().getFullYear();

  return json({
    raindropClientId: process.env.PUBLIC_RAINDROP_CLIENT_ID || '',
    raindropAuthRedirectUri: process.env.PUBLIC_RAINDROP_AUTH_REDIRECT_URI || '',
    ga4MeasurementId: process.env.GA4_MEASUREMENT_ID || '',
    sentryDsn: process.env.SENTRY_DSN || '',
    user,
    currentYear,
    hasConsented,
  });
};

export function ErrorBoundary() {
  const error = useRouteError();
  const matches = useMatches();

  // ユーティリティ関数を使ってユーザー情報を取得
  const user = getUserFromMatches(matches);

  // SentryにErrorBoundaryエラーを送信
  Sentry.captureRemixErrorBoundaryError(error);

  // クライアントサイドエラーをサーバーに送信（Remix内部エラーはフィルタリング）
  useEffect(() => {
    const sendErrorToServer = async () => {
      try {
        const message = error instanceof Error ? error.message : String(error);

        // Remix内部エラーはサーバーに送信しない（Sentryで補足されるため）
        if (!shouldReportErrorToServer(message)) {
          return;
        }

        const errorData = {
          message,
          stack: error instanceof Error ? error.stack : undefined,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          errorBoundary: true,
        };

        await fetch('/api/error', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(errorData),
        });
      } catch (fetchError) {
        // 送信に失敗してもコンソールログのみ
        console.error('Failed to send error to server:', fetchError);
      }
    };

    sendErrorToServer();
  }, [error]);

  // Remix v2では、export function LayoutがErrorBoundaryも自動的にラップするため
  // 手動で<Layout>を使う必要はない
  return <ErrorPage error={error} user={user} />;
}

export default function App() {
  const { hasConsented } = useLoaderData<typeof loader>();
  // nprogressを使ってページ遷移時にローディングバーを表示する
  const navigation = useNavigation();
  const fetchers = useFetchers();
  const fetchersIdle = fetchers.every((f) => f.state === 'idle');
  useEffect(() => {
    if (navigation.state === 'idle' && fetchersIdle) {
      NProgress.done();
    } else {
      NProgress.start();
    }
  }, [navigation, fetchersIdle]);

  // グローバルエラーハンドラーの設定（Remix内部エラーはフィルタリング）
  useEffect(() => {
    // unhandled promise rejection のキャッチ
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));

      // Remix内部エラーはサーバーに送信しない（Sentryで補足されるため）
      if (!shouldReportErrorToServer(error.message)) {
        return;
      }

      fetch('/api/error', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: error.message,
          stack: error.stack,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          errorBoundary: false,
        }),
      }).catch(() => {
        // 送信失敗時はコンソールログのみ
        console.error('Failed to send unhandled promise rejection to server:', error);
      });
    };

    // uncaught error のキャッチ
    const handleUncaughtError = (event: ErrorEvent) => {
      // Remix内部エラーはサーバーに送信しない（Sentryで補足されるため）
      if (!shouldReportErrorToServer(event.message)) {
        return;
      }

      fetch('/api/error', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: event.message,
          stack: `${event.filename}:${event.lineno}:${event.colno}`,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          errorBoundary: false,
        }),
      }).catch(() => {
        // 送信失敗時はコンソールログのみ
        console.error('Failed to send uncaught error to server:', event.error);
      });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleUncaughtError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleUncaughtError);
    };
  }, []);

  // Remix v2では、export function Layoutが自動的にAppをラップするため
  // App componentは子コンテンツのみを返す
  return (
    <>
      <Outlet />
      <Toaster />
      <CookieBanner hasConsented={hasConsented} />
    </>
  );
}
